/**
 * Country to default fiat currency and default spend amount mapping.
 * Used for auto-detecting and localizing the user's default deposit/withdraw amounts.
 */
export const COUNTRY_TO_FIAT_MAP: Record<string, { currency: string; defaultAmount: number }> = {
  US: { currency: 'USD', defaultAmount: 100 },
  MX: { currency: 'MXN', defaultAmount: 500 },
  IN: { currency: 'INR', defaultAmount: 5000 },
  GB: { currency: 'GBP', defaultAmount: 100 },
  DE: { currency: 'EUR', defaultAmount: 100 },
  FR: { currency: 'EUR', defaultAmount: 100 },
  IT: { currency: 'EUR', defaultAmount: 100 },
  ES: { currency: 'EUR', defaultAmount: 100 },
  NL: { currency: 'EUR', defaultAmount: 100 },
  BE: { currency: 'EUR', defaultAmount: 100 },
  AT: { currency: 'EUR', defaultAmount: 100 },
  FI: { currency: 'EUR', defaultAmount: 100 },
  IE: { currency: 'EUR', defaultAmount: 100 },
  PT: { currency: 'EUR', defaultAmount: 100 },
  GR: { currency: 'EUR', defaultAmount: 100 },
  CA: { currency: 'CAD', defaultAmount: 100 },
  AU: { currency: 'AUD', defaultAmount: 100 },
  NZ: { currency: 'NZD', defaultAmount: 100 },
  JP: { currency: 'JPY', defaultAmount: 10000 },
  KR: { currency: 'KRW', defaultAmount: 100000 },
  ZA: { currency: 'ZAR', defaultAmount: 1000 },
  SG: { currency: 'SGD', defaultAmount: 100 },
  HK: { currency: 'HKD', defaultAmount: 500 },
  CH: { currency: 'CHF', defaultAmount: 100 },
  TR: { currency: 'TRY', defaultAmount: 1000 },
  PH: { currency: 'PHP', defaultAmount: 2500 },
  ID: { currency: 'IDR', defaultAmount: 500000 },
  MY: { currency: 'MYR', defaultAmount: 250 },
  TH: { currency: 'THB', defaultAmount: 2000 },
  VN: { currency: 'VND', defaultAmount: 1000000 },
  // --- USA & Latin America ---
  AR: { currency: 'ARS', defaultAmount: 50000 },
  BO: { currency: 'BOB', defaultAmount: 500 },
  BR: { currency: 'BRL', defaultAmount: 500 },
  CL: { currency: 'CLP', defaultAmount: 50000 },
  CO: { currency: 'COP', defaultAmount: 200000 },
  CR: { currency: 'CRC', defaultAmount: 50000 },
  CU: { currency: 'CUP', defaultAmount: 500 },
  DO: { currency: 'DOP', defaultAmount: 5000 },
  EC: { currency: 'USD', defaultAmount: 100 },
  SV: { currency: 'USD', defaultAmount: 100 },
  GT: { currency: 'GTQ', defaultAmount: 500 },
  HT: { currency: 'HTG', defaultAmount: 10000 },
  HN: { currency: 'HNL', defaultAmount: 2000 },
  NI: { currency: 'NIO', defaultAmount: 2000 },
  PA: { currency: 'USD', defaultAmount: 100 },
  PY: { currency: 'PYG', defaultAmount: 500000 },
  PE: { currency: 'PEN', defaultAmount: 200 },
  UY: { currency: 'UYU', defaultAmount: 2000 },
  VE: { currency: 'VES', defaultAmount: 2000 },
};

export interface UserLocationResult {
  country: string;
  currency: string;
  defaultAmount: number;
}

import { walletApi } from '@/services/api';

/**
 * Helper to fetch geolocation defaults from Onramper API.
 */
async function fetchGeoLocation(): Promise<UserLocationResult | null> {
  try {
    const onramperDefaults = await walletApi.getDefaults();
    if (onramperDefaults && onramperDefaults.recommended && onramperDefaults.recommended.country) {
      const recommended = onramperDefaults.recommended;
      const country = recommended.country.toUpperCase();
      const currency = (
        recommended.source ||
        COUNTRY_TO_FIAT_MAP[country]?.currency ||
        'USD'
      ).toUpperCase();
      const defaultAmount =
        recommended.amount || COUNTRY_TO_FIAT_MAP[country]?.defaultAmount || 100;
      return { country, currency, defaultAmount };
    }
  } catch (e) {
    console.warn('[Geolocation] onramper failed:', e);
  }

  return null;
}

let locationPromise: Promise<UserLocationResult | null> | null = null;

/**
 * Auto-detects user location, country code, currency, and default amount.
 */
export async function detectUserLocation(): Promise<UserLocationResult> {
  const defaultFallback: UserLocationResult = {
    country: 'MX',
    currency: 'MXN',
    defaultAmount: 500,
  };

  if (typeof window === 'undefined') {
    return defaultFallback;
  }

  // Perform API lookup only if there's no active promise
  if (!locationPromise) {
    locationPromise = fetchGeoLocation();
  }

  const detected = await locationPromise;

  if (detected) {
    return detected;
  }

  // Reset promise on failure so it can retry later if needed
  locationPromise = null;
  return defaultFallback;
}
